import { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { useEffect } from 'react';

import { setHeadBaseUrl } from '@otovo/shared/components/Head/headContext';
import { useAnalytics } from '@otovo/shared/hooks/useAnalytics';
import { extractUtmOverridesFromSanity } from '@otovo/shared/lib/analytics/queryParams/extractUtmOverridesFromSanity';
import resolveTagManagerID from '@otovo/shared/lib/analytics/resolveTagManagerID';

import GoogleTagManagerAndCookieConsent from '@otovo/shared/lib/consent/GoogleTagManagerAndCookieConsent';
import '@otovo/shared/styles/globals.css';
import { Otovo$Config } from '@otovo/shared/types/otovoweb';
import { Sanity$Document } from '@otovo/shared/types/sanityTypes';
import workaroundBrowserExtensions from '@otovo/shared/utils/workaroundBrowserExtensions';
import { storeUtmData } from '../lib/storeUtmData';
import '../styles/tailwindConfig.css';

const SanityVisualEditing = dynamic(
  () => import('../components/Sanity/SanityVisualEditing'),
  { ssr: false }, // Visual editing only works client-side, and if draftMode=true
);

type SharedPageProps = {
  draftMode: boolean;
  token: string;
  config: Otovo$Config;
  sanityDocument?: Sanity$Document;
};

type ErrProps = {
  err: any;
};

function StorefrontApp({
  Component,
  pageProps,
  router,
  err,
}: AppProps<SharedPageProps> & ErrProps) {
  const { query, asPath, isReady } = router;
  const { config } = pageProps;

  const { draftMode } = pageProps;

  const gtmId = resolveTagManagerID(config);

  // Init analytics before using it.
  useAnalytics({ path: asPath, isGTMEnabled: !!gtmId });

  useEffect(() => {
    if (/Chrom/.test(window.navigator.userAgent)) {
      workaroundBrowserExtensions();
    }
  }, []);

  // Derive UTM data from query params and UTM from the Sanity page (if set).
  const { sanityDocument } = pageProps;
  const utmOverrides = extractUtmOverridesFromSanity(sanityDocument);

  useEffect(() => {
    if (isReady) {
      storeUtmData({
        ...utmOverrides,
        searchParams: query,
      });
    }
  }, [utmOverrides, query, isReady]);

  setHeadBaseUrl(config?.STOREFRONT_URL);

  return (
    <>
      {!!gtmId && (
        <GoogleTagManagerAndCookieConsent
          googleTagManagerId={gtmId}
          query={asPath.split('?')[1]}
        />
      )}
      <Component {...pageProps} err={err} />
      <div id="modal-root" />
      {draftMode && <SanityVisualEditing />}
    </>
  );
}

export default StorefrontApp;
